// ESZKÖZ-KATEGÓRIÁK ÉS -TÍPUSOK kezelője (Ilcsa #113) — beúszó sáv: a KÉT szintű fa (kategória → típusai) és
// alatta egy felvevő/szerkesztő űrlap. A sáv héja, a fa-vezetővonalak, a sor és a törlés-megerősítő a
// kiadás-kategóriák kezelőjével KÖZÖS (`shared/catalog-manager`).
//
// A törlés kapuja SZERVER-oldali (a felület őszinte tükre): a típust, amelyre bármely eszköz / igény hivatkozik
// (`usageCount`), nem lehet törölni — a kuka tiltva van, és megmondja, miért; a kategóriát addig nem, amíg van
// típusa. Az eszköz a STABIL kulcsot hordozza, ezért az átnevezés mindenhol azonnal látszik.
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { BilingualField } from '@/components/shared/bilingual-field';
import { CatalogDeleteDialog, CatalogRow, CatalogSheet } from '@/components/shared/catalog-manager';
import { SelectField, SwitchField, TextField } from '@/components/shared/form-fields';
import { treeGuides } from '@/components/shared/tree-guides';
import { Form } from '@/components/ui/form';
import {
  assetCatalogKey,
  catalogText,
  type AssetCategory,
  type AssetType,
} from '@/features/assets/catalog';
import {
  buildCatalogResolver,
  catalogFormFromCategory,
  catalogFormFromType,
  EMPTY_CATALOG_FORM,
  toCategoryBody,
  toTypeBody,
  type CatalogFormValues,
} from '@/features/assets/catalog-form';
import { api } from '@/lib/api';
import { useEntityMutation } from '@/lib/use-entity-mutation';

type CatalogResponse = Awaited<ReturnType<typeof api.assetCatalog.catalog.query>>;

/** A szerkesztett / törlendő sor: kategória vagy típus. */
type Target = { kind: 'category'; row: AssetCategory } | { kind: 'type'; row: AssetType };

export interface AssetCatalogSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  categories: readonly AssetCategory[];
  /** assets.manage — nélküle csak olvasható a fa (nincs űrlap, ceruza, kuka). */
  canManage: boolean;
}

export function AssetCatalogSheet({
  open,
  onOpenChange,
  categories,
  canManage,
}: AssetCatalogSheetProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [editing, setEditing] = useState<Target | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Target | null>(null);

  const form = useForm<CatalogFormValues>({
    resolver: buildCatalogResolver(t),
    mode: 'onTouched',
    defaultValues: EMPTY_CATALOG_FORM,
  });
  const { reset, handleSubmit, setError, watch } = form;
  const kind = watch('kind');

  const startEdit = (target: Target): void => {
    setEditing(target);
    reset(
      target.kind === 'category'
        ? catalogFormFromCategory(target.row)
        : catalogFormFromType(target.row),
    );
  };
  const startCreate = (): void => {
    setEditing(null);
    reset(EMPTY_CATALOG_FORM);
  };

  const saveMutation = useEntityMutation<CatalogFormValues, CatalogResponse>({
    mutationFn: (v) => {
      if (v.kind === 'category') {
        return editing?.kind === 'category'
          ? api.assetCatalog.categoryUpdate.mutation({
              params: { id: editing.row.id },
              body: toCategoryBody(v),
              headers: { 'if-match': String(editing.row.version) },
            })
          : api.assetCatalog.categoryCreate.mutation({ body: toCategoryBody(v) });
      }
      return editing?.kind === 'type'
        ? api.assetCatalog.typeUpdate.mutation({
            params: { id: editing.row.id },
            body: toTypeBody(v),
            headers: { 'if-match': String(editing.row.version) },
          })
        : api.assetCatalog.typeCreate.mutation({ body: toTypeBody(v) });
    },
    invalidateKeys: [assetCatalogKey, ['assets']],
    optimistic: {
      queryKey: assetCatalogKey,
      update: (old, v) => {
        if (old?.status !== 200) return old;
        const name = { hu: v.nameHu.trim(), en: v.nameEn.trim() === '' ? null : v.nameEn.trim() };
        const cats = old.body.categories;
        if (v.kind === 'category') {
          const prefix = v.numberPrefix.trim().toUpperCase();
          const next =
            editing?.kind === 'category'
              ? cats.map((c) =>
                  c.id === editing.row.id
                    ? { ...c, name, numberPrefix: prefix, isSoftware: v.isSoftware }
                    : c,
                )
              : [
                  ...cats,
                  {
                    id: `tmp-${String(cats.length)}`,
                    key: `tmp-${String(cats.length)}`,
                    name,
                    numberPrefix: prefix,
                    isSoftware: v.isSoftware,
                    position: cats.length,
                    version: 0,
                    types: [],
                  },
                ];
          return { ...old, body: { categories: next } };
        }
        // típus: átnevezés / áthelyezés, vagy új (ideiglenes id — a háttér-újratöltés cseréli)
        const withoutOld =
          editing?.kind === 'type'
            ? cats.map((c) => ({ ...c, types: c.types.filter((ty) => ty.id !== editing.row.id) }))
            : cats;
        const base: AssetType =
          editing?.kind === 'type'
            ? { ...editing.row, name, categoryId: v.categoryId }
            : {
                id: `tmp-type-${String(Date.now())}`,
                key: `tmp-type-${String(Date.now())}`,
                categoryId: v.categoryId,
                name,
                position: 9999,
                usageCount: 0,
                version: 0,
              };
        return {
          ...old,
          body: {
            categories: withoutOld.map((c) =>
              c.id === v.categoryId ? { ...c, types: [...c.types, base] } : c,
            ),
          },
        };
      },
    },
    onFieldErrors: (field, messages) => {
      const text = messages.join(' ');
      if (field === 'name') setError('nameHu', { message: text });
      else if (field === 'numberPrefix' || field === 'categoryId') {
        setError(field, { message: text });
      }
    },
    messages: {
      success: editing ? t('toast:assets.catalogUpdated') : t('toast:assets.catalogCreated'),
      conflict: t('toast:assets.conflict'),
      forbidden: t('toast:assets.forbidden'),
      validation: t('toast:assets.validation'),
      error: t('toast:assets.error'),
      unavailable: t('toast:assets.unavailable'),
    },
  });

  const deleteMutation = useEntityMutation<Target, CatalogResponse>({
    mutationFn: (target) =>
      target.kind === 'category'
        ? api.assetCatalog.categoryRemove.mutation({ params: { id: target.row.id } })
        : api.assetCatalog.typeRemove.mutation({ params: { id: target.row.id } }),
    invalidateKeys: [assetCatalogKey, ['assets']],
    optimistic: {
      queryKey: assetCatalogKey,
      update: (old, target) => {
        if (old?.status !== 200) return old;
        return {
          ...old,
          body: {
            categories:
              target.kind === 'category'
                ? old.body.categories.filter((c) => c.id !== target.row.id)
                : old.body.categories.map((c) => ({
                    ...c,
                    types: c.types.filter((ty) => ty.id !== target.row.id),
                  })),
          },
        };
      },
    },
    messages: {
      success: t('toast:assets.catalogDeleted'),
      conflict: t('toast:assets.catalogInUse'),
      forbidden: t('toast:assets.forbidden'),
      // a törlés 422-je: a sor használatban van (típus) / van típusa (kategória)
      validation: t('toast:assets.catalogInUse'),
      error: t('toast:assets.error'),
      unavailable: t('toast:assets.unavailable'),
    },
  });

  const onSubmit = handleSubmit((values) => {
    saveMutation.mutate(values, {
      onSuccess: () => {
        setEditing(null);
        reset(EMPTY_CATALOG_FORM);
      },
    });
  });

  // A lapos, mélység-első lista (kategória, majd a típusai) — a vezetővonalakat a közös `treeGuides` számolja.
  const entries: (Target & { depth: number })[] = categories.flatMap((c) => [
    { kind: 'category' as const, row: c, depth: 0 },
    ...c.types.map((ty) => ({ kind: 'type' as const, row: ty, depth: 1 })),
  ]);
  const guides = treeGuides(entries, (e) => e.depth);

  const categoryOptions = categories.map((c) => ({
    value: c.id,
    label: catalogText(c.name, lang),
  }));
  const kindOptions = [
    { value: 'category', label: t('assets.catalog.kindCategory') },
    { value: 'type', label: t('assets.catalog.kindType') },
  ];
  // használatban lévő típus nem helyezhető át másik kategóriába (az eszközök kategóriája elcsúszna)
  const typeInUse = editing?.kind === 'type' && editing.row.usageCount > 0;

  const rowLabel = (e: Target): string => catalogText(e.row.name, lang);

  return (
    <>
      <CatalogSheet
        open={open}
        onOpenChange={onOpenChange}
        title={t('assets.catalog.title')}
        description={t('assets.catalog.desc')}
        canManage={canManage}
        onSubmit={onSubmit}
        busy={saveMutation.isPending}
        editing={editing !== null}
        scrollToFormKey={editing}
        onCancelEdit={startCreate}
        labels={{
          add: t('assets.catalog.add'),
          saveEdit: t('assets.catalog.saveEdit'),
          cancel: t('common.cancel'),
          newTitle: t('assets.catalog.newTitle'),
          editTitle: t('assets.catalog.editTitle'),
        }}
        isEmpty={categories.length === 0}
        emptyLabel={t('assets.catalog.empty')}
        form={
          <Form {...form}>
            {editing === null && (
              <SelectField<CatalogFormValues>
                name="kind"
                label={t('assets.catalog.kind')}
                options={kindOptions}
              />
            )}
            <BilingualField<CatalogFormValues>
              variant="input"
              label={t('assets.catalog.name')}
              nameHu="nameHu"
              nameEn="nameEn"
              required
            />
            {kind === 'category' ? (
              <>
                <TextField<CatalogFormValues>
                  name="numberPrefix"
                  label={t('assets.catalog.numberPrefix')}
                  hint={t('assets.catalog.numberPrefixHint')}
                  required
                />
                <SwitchField<CatalogFormValues>
                  name="isSoftware"
                  label={t('assets.catalog.isSoftware')}
                  hint={t('assets.catalog.isSoftwareHint')}
                />
              </>
            ) : (
              <SelectField<CatalogFormValues>
                name="categoryId"
                label={t('assets.catalog.category')}
                required
                options={categoryOptions}
                disabled={typeInUse}
                {...(typeInUse ? { hint: t('assets.catalog.typeInUseMove') } : {})}
              />
            )}
          </Form>
        }
      >
        {entries.map((e, i) => {
          const label = rowLabel(e);
          const inUse = e.kind === 'type' && e.row.usageCount > 0;
          const hasTypes = e.kind === 'category' && e.row.types.length > 0;
          return (
            <CatalogRow
              key={`${e.kind}:${e.row.id}`}
              guides={guides[i] ?? []}
              label={label}
              emphasized={e.kind === 'category'}
              count={e.kind === 'type' ? e.row.usageCount : e.row.types.length}
              editLabel={t('assets.catalog.editAria', { name: label })}
              deleteLabel={t('assets.catalog.deleteAria', { name: label })}
              deleteDisabled={inUse || hasTypes}
              deleteDisabledReason={
                inUse ? t('assets.catalog.typeInUse') : t('assets.catalog.categoryHasTypes')
              }
              {...(canManage ? { onEdit: () => startEdit(e) } : {})}
              {...(canManage ? { onDelete: () => setDeleteTarget(e) } : {})}
            />
          );
        })}
      </CatalogSheet>

      <CatalogDeleteDialog
        targetLabel={deleteTarget ? rowLabel(deleteTarget) : null}
        onClose={() => setDeleteTarget(null)}
        title={
          deleteTarget?.kind === 'type'
            ? t('assets.catalog.deleteTypeTitle')
            : t('assets.catalog.deleteCategoryTitle')
        }
        describe={(name) => t('assets.catalog.deleteDesc', { name })}
        pending={deleteMutation.isPending}
        onConfirm={() => {
          if (!deleteTarget) return;
          deleteMutation.mutate(deleteTarget, { onSettled: () => setDeleteTarget(null) });
        }}
      />
    </>
  );
}
